import {
  HStack,
  Icon,
  IconButton,
  Text,
  Input,
  VStack,
  Box,
} from '@chakra-ui/react';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { StyledButton } from '@/components/ui/styled-button';
import { MenuRoot, MenuTrigger, MenuContent } from '@/components/ui/menu';
import { FiArrowUp, FiArrowDown, FiSearch, FiPlus } from 'react-icons/fi';
import { useState } from 'react';
import { ConversationFilters } from '@/features/conversations/conversations.types';
import { IoFilter } from 'react-icons/io5';

import { defaultConversationFilters } from './conversation-utils';
import { useCreateWhatsappConversation } from './useCreateWhatsappConversation';
// import { useCreateWhatsappConversation } from './useCreateWhatsappConversation';

interface FilterSortProps {
  onSortChange: (field: string, order: 'ASC' | 'DESC') => void;
  onFilterChange: (filters: Partial<ConversationFilters>) => void;
  currentFilters: ConversationFilters;
  sortOrder: 'ASC' | 'DESC';
  sortField: string;

  onConversationCreated: (conversationId: string) => void;
}

export function FilterSort({
  onSortChange,
  onFilterChange,
  currentFilters,
  sortOrder,
  sortField,
  onConversationCreated,
}: FilterSortProps) {
  // Add state for new conversation modal
  const { setIsNewConversationModalOpen } = useCreateWhatsappConversation();
  const [searchTerm, setSearchTerm] = useState(currentFilters.searchTerm || '');

  const handleOnOpenNewConversationModal = () => {
    console.log('handleOnOpenNewConversationModal');
    setIsNewConversationModalOpen(true);
  };
  // Local filter state to manage checkboxes
  const [localFilters, setLocalFilters] = useState({
    is_read: currentFilters.is_read,
    is_unread: currentFilters.is_read === false,
    has_agent_active: currentFilters.has_agent_active,
    has_attachments: currentFilters.has_attachments,
    isMember: currentFilters.isMember,
  });

  // Handle sort change
  const handleSortChange = (field: string) => {
    // If selecting the same field, toggle the order
    const newOrder =
      field === sortField && sortOrder === 'DESC' ? 'ASC' : 'DESC';

    onSortChange(field, newOrder);
  };

  // Handle checkbox toggle for other boolean filters
  const handleBooleanFilterChange = (filterName: keyof typeof localFilters) => {
    if (filterName === 'is_unread') {
      // Toggle unread filter - make mutually exclusive with is_read
      const newUnreadValue = !localFilters.is_unread;
      setLocalFilters({
        ...localFilters,
        is_unread: newUnreadValue,
        is_read: newUnreadValue === true ? false : localFilters.is_read, // Clear is_read if unread is selected
      });
    } else if (filterName === 'is_read') {
      // Toggle read filter - make mutually exclusive with is_unread
      const newReadValue = localFilters.is_read === true ? undefined : true;
      setLocalFilters({
        ...localFilters,
        is_read: newReadValue,
        is_unread: newReadValue === true ? false : localFilters.is_unread, // Clear is_unread if read is selected
      });
    } else {
      // Handle other boolean filters normally
      const newValue = localFilters[filterName] === true ? undefined : true;
      setLocalFilters({ ...localFilters, [filterName]: newValue });
    }
  };

  // Handle filter apply
  const handleApplyFilters = () => {
    onFilterChange({
      is_read: localFilters.is_read,
      has_agent_active: localFilters.has_agent_active,
      has_attachments: localFilters.has_attachments,
      isMember: localFilters.isMember,
      searchTerm,
    });
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setLocalFilters(defaultConversationFilters);
    setSearchTerm('');
    onFilterChange(defaultConversationFilters);
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange({ ...localFilters, searchTerm });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      localFilters.is_read === true ||
      localFilters.is_unread === true ||
      localFilters.has_agent_active !== undefined ||
      localFilters.has_attachments !== undefined ||
      localFilters.isMember !== undefined ||
      (searchTerm && searchTerm.trim() !== '')
    );
  };

  const filters = [
    {
      label: 'Read',
      value: 'is_read',
    },
    {
      label: 'Unread',
      value: 'is_unread',
    },
    {
      label: 'AI Agent Active',
      value: 'has_agent_active',
    },
    {
      label: 'Has Attachments',
      value: 'has_attachments',
    },
    {
      label: 'Member',
      value: 'isMember',
    },
  ] as const;

  return (
    <VStack gap={2} px={0} py={2} borderBottom="1px" borderColor="gray.200">
      <HStack justify="space-between" width="100%" px={0}>
        <Text fontSize="2xl" fontWeight="bold">
          Conversations
        </Text>
        <HStack gap={0}>
          {/* Filter Menu Dropdown */}
          <MenuRoot>
            <MenuTrigger asChild>
              <Box position="relative">
                <IconButton
                  aria-label="Filter conversations"
                  variant="ghost"
                  size="sm"
                >
                  <Icon as={IoFilter} />
                </IconButton>
                {hasActiveFilters() && (
                  <Box
                    position="absolute"
                    top="1"
                    right="1"
                    width="8px"
                    height="8px"
                    bg="blue.500"
                    borderRadius="full"
                    zIndex="1"
                  />
                )}
              </Box>
            </MenuTrigger>
            <MenuContent>
              <VStack align="stretch" gap={6} p={5} minW="280px" maxW="320px">
                <Text fontWeight="bold" fontSize="md" textAlign="center">
                  Filter Conversations
                </Text>

                <VStack gap={3} align="stretch">
                  {filters.map((filter) => (
                    <Field key={filter.value}>
                      <HStack justify="space-between" align="center" w="full">
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          cursor="pointer"
                          onClick={() =>
                            handleBooleanFilterChange(filter.value)
                          }
                          _hover={{ color: 'blue.600' }}
                        >
                          {filter.label}
                        </Text>
                        <Checkbox
                          size="sm"
                          checked={localFilters[filter.value] === true}
                          onCheckedChange={() =>
                            handleBooleanFilterChange(filter.value)
                          }
                        />
                      </HStack>
                    </Field>
                  ))}
                </VStack>

                <HStack gap={3} justify="center" pt={2}>
                  <StyledButton
                    variant="secondary"
                    onClick={handleResetFilters}
                    flex="1"
                  >
                    Reset
                  </StyledButton>
                  <StyledButton
                    variant="primary"
                    onClick={handleApplyFilters}
                    flex="1"
                  >
                    Apply
                  </StyledButton>
                </HStack>
              </VStack>
            </MenuContent>
          </MenuRoot>

          {/* Sort Menu - Simplified */}
          <IconButton
            aria-label="Sort conversations"
            variant="ghost"
            size="sm"
            onClick={() => handleSortChange('last_message_at')}
          >
            <Icon as={sortOrder === 'DESC' ? FiArrowDown : FiArrowUp} />
          </IconButton>

          {/* Add the New Conversation button */}
          <IconButton
            aria-label="New conversation"
            size="sm"
            colorPalette="gray"
            onClick={handleOnOpenNewConversationModal}
            variant="ghost"
          >
            <Icon as={FiPlus} />
          </IconButton>
        </HStack>
      </HStack>

      {/* Search box */}
      <form onSubmit={handleSearchSubmit} style={{ width: '100%' }}>
        <HStack
          width="100%"
          bg="gray.100"
          borderRadius="2xl"
          px={4}
          py={1}
          position="relative"
        >
          <Input
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={handleSearchChange}
            fontSize="md"
            border="none"
            flex="1"
            _focus={{
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
            }}
            _focusVisible={{
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
            }}
            _placeholder={{
              color: 'gray.500',
            }}
          />
          <IconButton
            aria-label="Search"
            size="sm"
            type="submit"
            variant="ghost"
            color="gray.500"
            borderRadius="full"
            _hover={{ bg: 'gray.200' }}
          >
            <Icon as={FiSearch} />
          </IconButton>
        </HStack>
      </form>
    </VStack>
  );
}
